import React from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { Tag } from '@teetsh/app/src/components/Tag';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { EyeIcon as Eye } from '@heroicons/react/24/outline';
import Img from 'components/Img';
import { Color, BadgeColor } from '@teetsh/app/src/types/colors';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';
import { StrapiEditor } from '@teetsh/app/src/types/cms';

interface TemplateItem {
  documentId?: string;
  id?: number;
  title?: string;
  name?: string;
  matieres?: string[];
  preview?: string;
  username?: string;
  editor?: StrapiEditor;
}

export interface TagData {
  key: string;
  label: string;
  color: Color | TagColorWithIntensity | BadgeColor;
}

interface Props {
  item: TemplateItem;
  tags: TagData[];
  onPreview?: () => void;
  onSelect: () => void;
  buttonText?: string;
  withPreviewButton?: boolean;
}

export default function TemplateCard(props: Props) {
  const { item, tags, onPreview, onSelect, buttonText, withPreviewButton } =
    props;
  const { t } = useTranslation(['emploiDuTemps', 'common']);

  // Handle different property names between item types
  const title = item.title || item.name || '';

  return (
    <div tw="bg-white rounded-lg flex flex-col h-full p-4">
      {item.preview && (
        <div
          onClick={onPreview}
          css={[
            tw`shrink-0 mb-3 transition duration-300 ease-in-out rounded-lg`,
            onPreview && tw`cursor-pointer hover:scale-[1.02]`,
            item.editor?.picture?.url && tw`relative`,
          ]}
        >
          {item.editor?.picture?.url && (
            <div
              css={[
                tw`absolute top-2 right-2 size-12 rounded-full box-content border-4 border-white bg-white bg-cover bg-center`,
                { backgroundImage: `url(${item.editor?.picture?.url})` },
              ]}
            ></div>
          )}
          <Img
            width={800}
            tw="w-full object-cover object-left-top h-40 rounded-lg"
            s3Path={new URL(item.preview).pathname}
          />
        </div>
      )}

      <div tw="mb-4">
        <h3 tw="mb-2">{title}</h3>
        {(item.matieres || []).length > 0 && (
          <div tw="mb-2">
            <span tw="font-semibold">
              {t(
                item.matieres!.length === 1
                  ? 'common:labels.Matière'
                  : 'common:labels.Matières',
              )}
              : 
            </span>
            {item.matieres!.join(', ')}
          </div>
        )}
        {(item.editor?.name || item.username) && (
          <p tw="text-base text-neutral-quiet">
            {t('common:by')} {item.editor?.name || item.username}
          </p>
        )}
      </div>

      <div tw="flex flex-wrap gap-2 mb-4">
        {tags.map(tag => (
          <Tag key={tag.key} size="sm" color={tag.color}>
            {tag.label}
          </Tag>
        ))}
      </div>

      <div tw="flex-grow flex items-end justify-end">
        <div tw="flex items-center gap-x-2">
          {withPreviewButton && (
            <Button
              minimal
              icon={Eye}
              onClick={onPreview}
              disabled={!item.preview || !onPreview}
            >
              {t('common:buttons.preview')}
            </Button>
          )}
          <Button
            outlined={!withPreviewButton}
            intent="primary"
            aria-label={`Utiliser ${title}`}
            onClick={onSelect}
          >
            {buttonText || t('common:buttons.use')}
          </Button>
        </div>
      </div>
    </div>
  );
}
